import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import * as Sentry from '@sentry/browser'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'

import subscriptions from 'modules/billing/store/collections/subscriptions'
import type { ImportedEmployee } from 'modules/employees/lib/ImportedEmployee'
import { useEmployeesImporter } from 'modules/employees/lib/employeesImporterContext'

interface Props {
  allEmployees: ImportedEmployee[]
  selectedEmployees: ImportedEmployee[]
  onClose: () => void
}

const ImportModal = ({ allEmployees, selectedEmployees, onClose }: Props) => {
  const api = React.useMemo(() => ApiClient(), [])
  const { token, templateId } = useEmployeesImporter()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { company, access } = useCurrentSession()

  const { running, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      if (!token) return

      const selected = selectedEmployees.map((item) => item.id)

      const updatedEmployees = selectedEmployees.filter((employee) => employee.type === 'update')
      const createdEmployees = selectedEmployees.filter((employee) => employee.type === 'new')

      track(trackingEvents.oldImportEmployees.confirmUploadOldEmployeesImporter, {
        employee_is_new: createdEmployees.length,
        employee_is_updates: updatedEmployees.length,
        companyId: company.id,
        acessId: access.id,
      })

      const { success, value } = await asyncAction(
        () =>
          api.post(`/onboarding_importer/imports/${token}`, {
            selected,
            template_id: templateId,
          }),
        {
          successMessage: i18n.t('import.success'),
        }
      )
      closePortal()

      if (success) {
        subscriptions.fetch()
        navigate(navigation.employees.fullPath())

        return
      }

      Sentry.captureException(value)
    },
    {
      successFlash: false,
    }
  )

  return (
    <Modal
      illustration="multiple-actions-upload"
      title={i18n.t('import.confirm.title', {
        selected: selectedEmployees.length,
        total: allEmployees.length,
      })}
      description={i18n.tp('import.confirm.description', {
        count: selectedEmployees.length,
      })}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button disabled={running} onClick={onClose} type="secondary">
            {i18n.t('import.confirm.cancel')}
          </Button>
          <Button disabled={running} submit>
            {i18n.t('import.confirm.submit')}
          </Button>
        </>
      }
    />
  )
}

export default observer(ImportModal)
